// Chemin: C:\gestion-copro\src\pages\admin\VerificationComptablePage.tsx
// Session 67 : Création module Vérification Comptable
// Session 68 : Ajout modale suppression élégante avec double sécurité
// Référence : CADRAGE_MODULE_VERIFICATION_COMPTABLE_V1.md

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';
import {
  getVerificationsResumes,
  createVerification,
  deleteVerification,
} from '../../services/verification.service';
import {
  VerificationResume,
  STATUT_VERIFICATION_LABELS,
  STATUT_VERIFICATION_COLORS,
  getScoreColor,
  getScoreLabel,
} from '../../types/verification.types';
import {
  Calculator,
  Plus,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Trash2,
  FileText,
  Calendar,
  Loader2,
  Shield,
  BookOpen,
  Eye,
  AlertOctagon,
} from 'lucide-react';

export default function VerificationComptablePage() {
  const navigate = useNavigate();
  const { coproprieteId } = useCopropriete();
  const { firebaseUser, userProfile } = useAuth();

  // États
  const [verifications, setVerifications] = useState<VerificationResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [creating, setCreating] = useState(false);

  // Formulaire nouvelle vérification
  const [newExercice, setNewExercice] = useState(new Date().getFullYear().toString());
  const [newTitre, setNewTitre] = useState('');

  // États suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VerificationResume | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Chargement des vérifications
  useEffect(() => {
    if (!coproprieteId) return;

    const loadVerifications = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getVerificationsResumes(coproprieteId);
        setVerifications(data);
      } catch (err) {
        console.error('Erreur chargement vérifications:', err);
        setError('Impossible de charger les vérifications');
      } finally {
        setLoading(false);
      }
    };

    loadVerifications();
  }, [coproprieteId]);

  // Créer une nouvelle vérification
  const handleCreate = async () => {
    if (!coproprieteId || !firebaseUser || !userProfile) return;
    if (!newExercice.trim()) {
      setError('L\'exercice est obligatoire');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const verificationId = await createVerification(
        {
          coproprieteId,
          exercice: newExercice.trim(),
          titre: newTitre.trim() || undefined,
        },
        firebaseUser.uid,
        userProfile.prenom ? `${userProfile.prenom} ${userProfile.nom || ''}`.trim() : userProfile.email || 'Utilisateur'
      );

      // Naviguer vers la page de détail
      navigate(`/admin/comptabilite/${verificationId}`);
    } catch (err) {
      console.error('Erreur création vérification:', err);
      setError('Impossible de créer la vérification');
      setCreating(false);
    }
  };

  // Ouvrir modale suppression
  const openDeleteModal = (verification: VerificationResume) => {
    setDeleteTarget(verification);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  // Supprimer une vérification
  const handleDelete = async () => {
    if (!coproprieteId || !deleteTarget) return;
    if (deleteConfirmText !== deleteTarget.exercice) return;

    setDeleting(true);

    try {
      await deleteVerification(coproprieteId, deleteTarget.id);
      setVerifications((prev) => prev.filter((v) => v.id !== deleteTarget.id));
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError('Impossible de supprimer la vérification');
    } finally {
      setDeleting(false);
    }
  };

  // Formatage date
  const formatDate = (timestamp: { toDate: () => Date }) => {
    return timestamp.toDate().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Rendu carte vérification
  const renderVerificationCard = (v: VerificationResume) => (
    <div
      key={v.id}
      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between">
        {/* Infos principales */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Exercice {v.exercice}
            </h3>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                STATUT_VERIFICATION_COLORS[v.statut]
              }`}
            >
              {STATUT_VERIFICATION_LABELS[v.statut]}
            </span>
          </div>

          {v.titre && (
            <p className="text-sm text-gray-600 mb-2">{v.titre}</p>
          )}

          {/* Compteurs */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span>{v.controlesOK} OK</span>
            </div>
            <div className="flex items-center gap-1 text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <span>{v.controlesAttention} attention</span>
            </div>
            <div className="flex items-center gap-1 text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{v.controlesAnomalie} anomalie{v.controlesAnomalie > 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Métadonnées */}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(v.dateCreation)}</span>
            </div>
            <div className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              <span>{v.nombreDocuments} document{v.nombreDocuments > 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Score et actions */}
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <div className={`text-2xl font-bold ${getScoreColor(v.scoreConformite)}`}>
              {v.scoreConformite}/100
            </div>
            <div className="text-xs text-gray-500">{getScoreLabel(v.scoreConformite)}</div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate(`/admin/comptabilite/${v.id}`)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            >
              Voir détail
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => openDeleteModal(v)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // État vide enrichi avec pédagogie
  const renderEmptyState = () => (
    <div className="space-y-8">
      {/* Bandeau contexte */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Pourquoi contrôler les comptes du syndic ?
            </h2>
            <p className="text-gray-600 mb-3">
              Selon la DGCCRF, <strong>65 à 72% des syndics contrôlés présentent des anomalies</strong> : 
              erreurs de répartition, facturations abusives, travaux non votés...
            </p>
            <p className="text-gray-600">
              Le conseil syndical a le <strong>droit et le devoir</strong> de vérifier les comptes 
              (art. 21 loi 10 juillet 1965). SENTINELLE automatise ce contrôle fastidieux.
            </p>
          </div>
        </div>
      </div>

      {/* Mode d'emploi */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-gray-500" />
          Comment ça marche ?
        </h3>
        
        <div className="grid md:grid-cols-4 gap-4">
          {/* Étape 1 */}
          <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mb-3">
              1
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Créez une vérification</h4>
            <p className="text-sm text-gray-500">
              Choisissez l'exercice comptable à contrôler
            </p>
          </div>
          
          {/* Étape 2 */}
          <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mb-3">
              2
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Importez les documents</h4>
            <p className="text-sm text-gray-500">
              Annexes comptables, appels de fonds, budget, factures...
            </p>
          </div>
          
          {/* Étape 3 */}
          <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mb-3">
              3
            </div>
            <h4 className="font-medium text-gray-900 mb-1">SENTINELLE analyse</h4>
            <p className="text-sm text-gray-500">
              18 contrôles automatiques : totaux, doublons, cohérence...
            </p>
          </div>
          
          {/* Étape 4 */}
          <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mb-3">
              4
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Agissez</h4>
            <p className="text-sm text-gray-500">
              Demandez des justificatifs, créez une affaire, préparez l'AG
            </p>
          </div>
        </div>
      </div>

      {/* Ce que SENTINELLE vérifie */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Eye className="h-5 w-5 text-gray-500" />
          Ce que SENTINELLE vérifie pour vous
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="font-medium text-gray-900">Cohérence interne</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Totaux vs détail des lignes</li>
              <li>• Répartition des tantièmes</li>
              <li>• Doublons de factures</li>
              <li>• Dates hors exercice</li>
            </ul>
          </div>
          
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span className="font-medium text-gray-900">Rapprochements</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Factures vs comptabilité</li>
              <li>• Contrats vs facturation</li>
              <li>• Budget N vs N-1</li>
              <li>• Relevé bancaire vs écritures</li>
            </ul>
          </div>
          
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="font-medium text-gray-900">Alertes métier</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Surfacturation potentielle</li>
              <li>• Travaux non votés en AG</li>
              <li>• Honoraires hors forfait excessifs</li>
              <li>• Impayés non recouvrés</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Call to action */}
      <div className="text-center py-6">
        <button
          onClick={() => setShowNewModal(true)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium shadow-lg shadow-blue-200"
        >
          <Plus className="h-5 w-5" />
          Créer ma première vérification
        </button>
        <p className="text-sm text-gray-500 mt-3">
          Commencez par l'exercice en cours ou le dernier exercice clos
        </p>
      </div>
    </div>
  );

  // Modal création
  const renderNewModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Nouvelle vérification comptable
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Exercice *
            </label>
            <input
              type="text"
              value={newExercice}
              onChange={(e) => setNewExercice(e.target.value)}
              placeholder="2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Année de l'exercice comptable à vérifier
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre (optionnel)
            </label>
            <input
              type="text"
              value={newTitre}
              onChange={(e) => setNewTitre(e.target.value)}
              placeholder="Ex: Contrôle pré-AG 2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => {
              setShowNewModal(false);
              setError(null);
            }}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={creating}
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !newExercice.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Créer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Modal suppression avec double sécurité
  const renderDeleteModal = () => {
    if (!deleteTarget) return null;

    const isConfirmValid = deleteConfirmText === deleteTarget.exercice;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          {/* Header avec icône d'alerte */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertOctagon className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Supprimer la vérification
            </h2>
          </div>

          {/* Avertissement */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">
              <strong>Attention :</strong> Cette action est irréversible. Tous les contrôles 
              et résultats associés seront définitivement supprimés.
            </p>
          </div>

          {/* Détails de ce qui sera supprimé */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600 mb-2">Vous allez supprimer :</p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-gray-400" />
                <span><strong>Exercice {deleteTarget.exercice}</strong>{deleteTarget.titre && ` — ${deleteTarget.titre}`}</span>
              </li>
              <li className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <span>{deleteTarget.nombreDocuments} document{deleteTarget.nombreDocuments > 1 ? 's' : ''} lié{deleteTarget.nombreDocuments > 1 ? 's' : ''}</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-gray-400" />
                <span>{deleteTarget.controlesOK + deleteTarget.controlesAttention + deleteTarget.controlesAnomalie} contrôle{(deleteTarget.controlesOK + deleteTarget.controlesAttention + deleteTarget.controlesAnomalie) > 1 ? 's' : ''}</span>
              </li>
            </ul>
          </div>

          {/* Double confirmation */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pour confirmer, tapez <strong className="text-red-600">{deleteTarget.exercice}</strong> ci-dessous :
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={deleteTarget.exercice}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                deleteConfirmText && !isConfirmValid ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteTarget(null);
                setDeleteConfirmText('');
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={deleting}
            >
              Annuler
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting || !isConfirmValid}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Supprimer définitivement
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Calculator className="h-7 w-7 text-blue-600" />
            Vérification Comptable
          </h1>
          <p className="text-gray-500 mt-1">
            Contrôlez les documents comptables du syndic avec SENTINELLE
          </p>
        </div>

        {verifications.length > 0 && (
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Nouvelle vérification
          </button>
        )}
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-500">Chargement...</span>
        </div>
      ) : error && verifications.length === 0 ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : verifications.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="space-y-4">
          {verifications.map(renderVerificationCard)}
        </div>
      )}

      {/* Modales */}
      {showNewModal && renderNewModal()}
      {showDeleteModal && renderDeleteModal()}
    </div>
  );
}
